interface OrderTimelineProps {
  status: string | null;
  createdAt?: string;
  trackingNumber?: string | null;
  compact?: boolean;
}

const STEPS: { key: string; label: string; icon: string; hint: string }[] = [
  {
    key: "pending_payment",
    label: "Aguardando",
    icon: "ri-time-line",
    hint: "Aguardando pagamento",
  },
  {
    key: "paid",
    label: "Pago",
    icon: "ri-bank-card-line",
    hint: "Pagamento recebido",
  },
  {
    key: "processing",
    label: "Em produção",
    icon: "ri-tools-line",
    hint: "Ateliê produzindo sua peça",
  },
  {
    key: "shipped",
    label: "Enviado",
    icon: "ri-truck-line",
    hint: "A caminho de você",
  },
  {
    key: "delivered",
    label: "Entregue",
    icon: "ri-checkbox-circle-line",
    hint: "Aproveite sua peça",
  },
];

function stepIndexFor(status: string | null): number {
  switch (status) {
    case "pending_payment":
      return 0;
    case "paid":
      return 1;
    case "processing":
      return 2;
    case "shipped":
      return 3;
    case "delivered":
      return 4;
    default:
      return -1;
  }
}

function formatStepDate(iso?: string) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("pt-BR", {
    month: "short",
    day: "numeric",
  });
}

export default function OrderTimeline({
  status,
  createdAt,
  trackingNumber,
  compact = false,
}: OrderTimelineProps) {
  const cancelled = status === "cancelled" || status === "refunded";
  const currentIdx = stepIndexFor(status);

  if (cancelled) {
    return (
      <div className="rounded-2xl border border-background-200/70 bg-background-100/60 p-5 md:p-6">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-full bg-background-200/80 text-foreground-700 flex items-center justify-center">
            <i className="ri-close-circle-line text-[22px]"></i>
          </div>
          <div>
            <p className="text-[11.5px] uppercase tracking-[0.18em] text-foreground-500 mb-0.5">
              Status do pedido
            </p>
            <p className="font-heading text-foreground-950 text-lg">
              {status === "refunded" ? "Reembolsado" : "Cancelado"}
            </p>
            <p className="text-[12.5px] text-foreground-600 mt-0.5">
              {status === "refunded"
                ? "Seu reembolso já foi feito — pode levar alguns dias para aparecer."
                : "Este pedido foi cancelado antes do envio."}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`rounded-2xl border border-background-200/70 bg-background-50 ${
        compact ? "p-4 md:p-5" : "p-5 md:p-7"
      }`}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-5 md:mb-6">
        <div>
          <p className="text-[11.5px] uppercase tracking-[0.18em] text-foreground-500 mb-0.5">
            Progresso do pedido
          </p>
          <p className="font-heading text-foreground-950 text-lg md:text-xl">
            {currentIdx >= 0 ? STEPS[currentIdx].hint : "Aguardando pagamento"}
          </p>
        </div>
        {trackingNumber && currentIdx >= 3 && (
          <a
            href="#"
            onClick={(e) => e.preventDefault()}
            className="hidden sm:inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full bg-foreground-950 text-background-50 text-[12.5px] font-medium cursor-pointer whitespace-nowrap hover:bg-foreground-800 transition-colors"
          >
            <i className="ri-route-line"></i>
            Rastrear {trackingNumber}
          </a>
        )}
      </div>

      {/* Steps */}
      <div className="relative">
        {/* Base track line */}
        <div className="absolute left-0 right-0 top-5 h-[2px] bg-background-200/80"></div>
        {/* Filled line */}
        <div
          className="absolute left-0 top-5 h-[2px] bg-foreground-950 transition-all duration-500"
          style={{
            width:
              currentIdx <= 0
                ? "0%"
                : currentIdx >= STEPS.length - 1
                ? "100%"
                : `${(currentIdx / (STEPS.length - 1)) * 100}%`,
          }}
        ></div>

        <ol className="relative grid grid-cols-5 gap-1">
          {STEPS.map((step, idx) => {
            const completed = idx < currentIdx;
            const active = idx === currentIdx;
            const upcoming = idx > currentIdx;

            return (
              <li
                key={step.key}
                className="flex flex-col items-center text-center"
              >
                {/* Dot */}
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all duration-300 ${
                    active
                      ? "bg-foreground-950 border-foreground-950 text-background-50 ring-4 ring-foreground-950/10"
                      : completed
                      ? "bg-foreground-950 border-foreground-950 text-background-50"
                      : "bg-background-50 border-background-300 text-foreground-500"
                  }`}
                >
                  {completed ? (
                    <i className="ri-check-line text-[16px]"></i>
                  ) : (
                    <i className={`${step.icon} text-[16px]`}></i>
                  )}
                </div>

                {/* Label */}
                <p
                  className={`mt-2.5 text-[12px] md:text-[12.5px] font-medium leading-tight ${
                    upcoming ? "text-foreground-500" : "text-foreground-950"
                  }`}
                >
                  {step.label}
                </p>

                {/* Sub-line */}
                {!compact && (
                  <p
                    className={`mt-0.5 text-[11px] leading-snug max-w-[110px] ${
                      upcoming ? "text-foreground-500" : "text-foreground-700"
                    }`}
                  >
                    {active
                      ? createdAt && idx === 0
                        ? formatStepDate(createdAt)
                        : "Em andamento"
                      : completed
                      ? "Concluído"
                      : "A seguir"}
                  </p>
                )}

                {/* Active pulse pill */}
                {active && !compact && (
                  <span className="mt-2 inline-flex items-center gap-1 px-2 h-5 rounded-full bg-accent-100 text-accent-900 text-[10.5px] font-medium uppercase tracking-[0.14em]">
                    <span className="w-1.5 h-1.5 rounded-full bg-accent-500 animate-pulse"></span>
                    Agora
                  </span>
                )}
              </li>
            );
          })}
        </ol>
      </div>
    </div>
  );
}